import React, { useRef, useMemo, useEffect, use, useState, useLayoutEffect } from 'react'
import {
  styled,
  Provider as ThemeProvider,
  darkTheme,
  Variation,
  lightTheme,
  overridePrimaryColor,
  css,
  tokensCss,
} from '@chilipiper/design-system'
import { AppearanceSettingsValues } from '@chilipiper/api-type-def/src/playbook'
import { isDarkColor, parseColorToRgb } from '@chilipiper/utils'
import { defaultAppearanceSettings } from '../constants'
import { useGetAppearanceSettings } from '../api-queries/botAndDataFieldsVisibilitySettings'

const StyledThemeWrapper = styled.div<{ primaryColor?: string; primaryContrastColor: string }>`
  display: contents;
  --primary-contrast-color: ${({ primaryContrastColor }) => primaryContrastColor};
  --primary-color: ${({ primaryColor }) => primaryColor ?? '84, 61, 255'};
  ${tokensCss}
  --fg-major-on-color: rgb(var(--primary-contrast-color));
  ${({ primaryColor }) => {
    if (primaryColor) {
      return css`
        --base-purple-8: rgba(${primaryColor}, 0.08);
        --base-purple-16: rgba(${primaryColor}, 0.16);
        --base-purple-24: rgba(${primaryColor}, 0.24);
        --base-purple-32: rgba(${primaryColor}, 0.32);
        --base-purple-40: rgba(${primaryColor}, 0.4);
        --base-purple-48: rgba(${primaryColor}, 0.48);
        --base-purple-56: rgba(${primaryColor}, 0.56);
        --base-purple-64: rgba(${primaryColor}, 0.64);
        --base-purple-80: rgba(${primaryColor}, 0.8);
        --base-purple-100: rgba(${primaryColor}, 1);
      `
    }
  }}
`

const AppearanceSettingsContext = React.createContext(defaultAppearanceSettings)

export const useAppearanceSettings = () => {
  return use(AppearanceSettingsContext)
}

interface Props extends React.PropsWithChildren {
  appearanceSettings?: Partial<AppearanceSettingsValues>
  handleAppearanceSettingsChange?: (appearanceSettings: AppearanceSettingsValues) => void
  handleSetTriggers?: (triggers: AppearanceSettingsValues['customTriggers']['published']) => void
  tenantId: string
  workspaceId: string
}

export const ChiliTheme = ({
  children,
  workspaceId,
  tenantId,
  appearanceSettings: appearanceSettingsProps,
  handleAppearanceSettingsChange,
  handleSetTriggers,
}: Props) => {
  const { appearanceSettings: appearanceSettingsRequest, isLoading } = useGetAppearanceSettings(
    tenantId,
    workspaceId
  )

  const ref = useRef<HTMLDivElement | null>(null)
  const [primaryContrastColor, setPrimaryContrastColor] = useState('255, 255, 255')

  const appearanceSettings = useMemo(() => {
    if (isLoading) {
      return
    }

    return {
      ...defaultAppearanceSettings,
      ...(appearanceSettingsRequest?.settings ?? {}),
      ...(appearanceSettingsProps ?? {}),
    } as AppearanceSettingsValues
  }, [appearanceSettingsRequest, appearanceSettingsProps, isLoading])

  const settingsPrimaryColor = useMemo(() => {
    if (appearanceSettings?.actionColor) {
      const rgbActionColor = parseColorToRgb(appearanceSettings.actionColor)
      return `${rgbActionColor.r}, ${rgbActionColor.g}, ${rgbActionColor.b}`
    }
  }, [appearanceSettings?.actionColor])

  useEffect(() => {
    if (appearanceSettings && handleAppearanceSettingsChange) {
      handleAppearanceSettingsChange(appearanceSettings)
    }
    if (appearanceSettings?.customTriggers && handleSetTriggers) {
      handleSetTriggers(appearanceSettings.customTriggers.published)
    }
  }, [appearanceSettings])

  useLayoutEffect(() => {
    if (ref.current) {
      const isDark = isDarkColor(`rgb(${settingsPrimaryColor})`)
      setPrimaryContrastColor(isDark ? '255, 255, 255' : '38, 52, 64')
    }
  }, [settingsPrimaryColor])

  const isDarkMode = appearanceSettings?.themeMode === 'dark'

  const baseTheme = isDarkMode ? darkTheme : lightTheme

  return appearanceSettings ? (
    <ThemeProvider
      variation={
        settingsPrimaryColor
          ? applyPrimaryColorOverrides(baseTheme, settingsPrimaryColor)
          : baseTheme
      }
    >
      <StyledThemeWrapper
        ref={ref}
        primaryColor={settingsPrimaryColor}
        primaryContrastColor={primaryContrastColor}
        data-chili-css={isDarkMode ? 'chat-dark-theme' : 'chat-light-theme'}
      >
        <AppearanceSettingsContext value={appearanceSettings}>{children}</AppearanceSettingsContext>
      </StyledThemeWrapper>
    </ThemeProvider>
  ) : null
}

const applyPrimaryColorOverrides = (theme: Variation, primaryColor: string): Variation => {
  return {
    ...theme,
    theme: {
      ...theme.theme,
      shadows: overridePrimaryColor(theme.theme.shadows, primaryColor),
    },
  }
}
